import { Bell } from 'lucide-react';
import { Card, EmptyState, NativeSelect, toast } from '@vit/ui';
import type { NotificationPreference } from '@vit/contracts';
import { useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { QueryState, TimeAgo } from '../../components/common.js';

const EVENTS = ['REQUEST_RECEIVED', 'REQUEST_NEEDS_INFORMATION', 'REQUEST_ACCEPTED', 'REQUEST_REJECTED', 'REQUEST_WORK_STARTED', 'REQUEST_RESOLVED', 'REQUEST_NEEDS_ATTENTION', 'WORK_ASSIGNED', 'COMMENT_ADDED', 'MENTIONED'] as const;

/** In-app inbox plus per-event email frequency (immediate, daily digest or muted). */
export function NotificationPreferences() {
  const { t } = useTranslation();
  const inbox = useApiQuery('listNotifications', { query: { limit: '30' } }, { retry: false });
  const prefs = useApiQuery('getNotificationPreferences', {}, { retry: false });
  const save = useApiMutation('setNotificationPreferences', { invalidate: ['getNotificationPreferences'] });
  const markRead = useApiMutation('markNotificationsRead', { invalidate: ['listNotifications'] });
  const current = new Map((prefs.data?.data ?? []).map((p) => [`${p.channel}:${p.event}`, p.frequency]));
  const set = async (event: string, frequency: string) => {
    const next: NotificationPreference[] = [...(prefs.data?.data ?? []).filter((p) => !(p.channel === 'EMAIL' && p.event === event)), { channel: 'EMAIL', event: event as 'REQUEST_RECEIVED', frequency: frequency as 'IMMEDIATE' }];
    try {
      await save.mutateAsync({ body: { preferences: next } });
      toast.success(t('common.changesSaved'));
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <QueryState query={inbox} empty={inbox.data?.data.length === 0 ? <EmptyState icon={<Bell />} title={t('common.noResults')} /> : undefined}>
          <ul className="divide-y divide-border">
            {(inbox.data?.data ?? []).map((n) => (
              <li key={n.id} className={`flex items-start gap-3 px-4 py-3 ${n.readAt ? '' : 'bg-accent-soft/20'}`}>
                <Bell className="mt-0.5 size-4 text-fg-subtle" />
                <div className="min-w-0 flex-1">
                  <a href={n.link ?? '#'} className="text-sm font-medium hover:underline" onClick={() => void markRead.mutateAsync({ body: { ids: [n.id] } })}>
                    {n.title}
                  </a>
                  <p className="text-sm text-fg-muted">{n.body}</p>
                </div>
                <span className="text-xs">
                  <TimeAgo iso={n.createdAt} />
                </span>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
      <Card className="p-5">
        <QueryState query={prefs}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-subtle">
                <th className="py-2 font-medium">Event</th>
                <th className="py-2 font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {EVENTS.map((event) => (
                <tr key={event} className="border-t border-border">
                  <td className="py-2">{event.replace(/_/g, ' ').toLowerCase()}</td>
                  <td className="py-2">
                    <NativeSelect className="h-7 w-40" value={current.get(`EMAIL:${event}`) ?? (event.startsWith('REQUEST_') ? 'IMMEDIATE' : 'MUTED')} onChange={(e) => void set(event, e.target.value)} aria-label={event}>
                      <option value="IMMEDIATE">Immediate</option>
                      <option value="DAILY_DIGEST">Daily digest</option>
                      <option value="MUTED">Muted</option>
                    </NativeSelect>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>
    </div>
  );
}
